import { AkitaPlugin, EntityState, isFunction, logAction, Queries } from '@datorama/akita';
import { AbstractGitEntityState, AbstractGitEntityStore } from '@y42/feature/git/state';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, pairwise } from 'rxjs/operators';

export enum OperationType {
  Undo = 'Undo',
  Redo = 'Redo',
}

export interface StateHistoryParams {
  maxAge: number;
  watchProperty?: string;
  comparator?: (prevState: AbstractGitEntityState<never>, currentState: AbstractGitEntityState<never>) => boolean;
}

export interface History<AbstractGitEntityState> {
  past: AbstractGitEntityState[];
  present: AbstractGitEntityState | null;
  future: AbstractGitEntityState[];
}

// REFACTORME: extend & override StateHistoryPlugin from @datorama/akita instead
export class StateHistoryGitPlugin extends AkitaPlugin {
  constructor(
    protected query: Queries<EntityState>,
    protected store: AbstractGitEntityStore,
    private readonly params: StateHistoryParams = {
      maxAge: 10,
      watchProperty: '',
    },
    private readonly _entityId?: string,
  ) {
    super(query, {
      resetFn: () => this.clear(),
    });

    params.maxAge = params.maxAge ? params.maxAge : 10;
    params.comparator = params.comparator || (() => true);

    this.activate();
  }

  /** Allow skipping an update from outside */
  private skip = false;

  private history: History<AbstractGitEntityState> = {
    past: [],
    present: null,
    future: [],
  };

  /** Skip the update when redo/undo */
  private skipUpdate = false;
  private subscription!: Subscription;

  /* Subjects for supporting observable hasPast$ and hasFuture$ */
  private hasPastSubject!: BehaviorSubject<boolean>;
  private _hasPast$!: Observable<boolean>;
  private hasFutureSubject!: BehaviorSubject<boolean>;
  private _hasFuture$!: Observable<boolean>;

  /**
   * Observable stream representing whether the history plugin has an available past
   *
   */
  get hasPast$(): Observable<boolean> {
    return this._hasPast$;
  }

  /**
   * Observable stream representing whether the history plugin has an available future
   *
   */
  get hasFuture$(): Observable<boolean> {
    return this._hasFuture$;
  }

  get hasPast() {
    return this.history.past.length > 0;
  }

  get hasFuture() {
    return this.history.future.length > 0;
  }

  activate() {
    this.hasPastSubject = new BehaviorSubject<boolean>(false);
    this._hasPast$ = this.hasPastSubject.asObservable().pipe(distinctUntilChanged());
    this.hasFutureSubject = new BehaviorSubject<boolean>(false);
    this._hasFuture$ = this.hasFutureSubject.asObservable().pipe(distinctUntilChanged());

    this.history.present = this.getSource(this._entityId, this.property);

    this.subscription = this.selectSource(this._entityId, this.property)
      .pipe(pairwise())
      .subscribe(([past, present]) => {
        if (this.skip) {
          this.skip = false;
          return;
        }
        /**
         *  comparator: (prev, current) => isEqual(prev, current) === false
         */
        if (this.params.comparator) {
          const shouldUpdate = this.params.comparator(past, present);
          if (!this.skipUpdate && shouldUpdate) {
            if (this.history.past.length === this.params.maxAge) {
              this.history.past = this.history.past.slice(1);
            }
            this.history.past = [...this.history.past, past];
            this.history.present = present;
            this.updateHasHistory();
          }
        }
      });
  }

  undo() {
    if (this.history.past.length > 0) {
      const { past, present } = this.history;
      const previous = past[past.length - 1];
      this.history.past = past.slice(0, past.length - 1);
      this.history.present = previous ?? null;
      this.history.future = [present!, ...this.history.future];
      this.update();
    }
  }

  redo() {
    if (this.history.future.length > 0) {
      const { past, present } = this.history;
      const next = this.history.future[0];
      const newFuture = this.history.future.slice(1);
      this.history.past = [...past!, present!];
      this.history.present = next ?? null;
      this.history.future = newFuture;
      this.update(OperationType.Redo);
    }
  }

  jumpToPast(index: number) {
    if (index < 0 || index >= this.history.past.length) {
      return;
    }

    const { past, future, present } = this.history;
    /**
     *
     * const past = [1, 2, 3, 4, 5];
     * const present = 6;
     * const future = [7, 8, 9];
     * const index = 2;
     *
     * newPast = past.slice(0, index) = [1, 2];
     * newPresent = past[index] = 3;
     * newFuture = [...past.slice(index + 1),present, ...future] = [4, 5, 6, 7, 8, 9];
     *
     */
    const newPast = past.slice(0, index);
    const newFuture = [...past.slice(index + 1), present, ...future];
    const newPresent = past[index];
    this.history.past = newPast;
    this.history.present = newPresent ?? null;
    this.history.future = newFuture as AbstractGitEntityState[];
    this.update();
  }

  jumpToFuture(index: number) {
    if (index < 0 || index >= this.history.future.length) {
      return;
    }

    const { past, future, present } = this.history;
    /**
     *
     * const past = [1, 2, 3, 4, 5];
     * const present = 6;
     * const future = [7, 8, 9, 10]
     * const index = 1
     *
     * newPast = [...past, present, ...future.slice(0, index) = [1, 2, 3, 4, 5, 6, 7];
     * newPresent = future[index] = 8;
     * newFuture = futrue.slice(index+1) = [9, 10];
     *
     */

    const newPast = [...past, present, ...future.slice(0, index)];
    const newPresent = future[index];
    const newFuture = future.slice(index + 1);
    this.history.past = newPast as AbstractGitEntityState[];
    this.history.present = newPresent ?? null;
    this.history.future = newFuture;
    this.update(OperationType.Redo);
  }

  /**
   *
   * jump n steps in the past or forward
   *
   */
  jump(n: number) {
    if (n > 0) {
      return this.jumpToFuture(n - 1);
    }
    if (n < 0) {
      return this.jumpToPast(this.history.past.length + n);
    }
  }

  /**
   * Clear the history
   *
   * @param customUpdateFn Callback function for only clearing part of the history
   *
   * @example
   *
   * stateHistory.clear((history) => {
   *  return {
   *    past: history.past,
   *    present: history.present,
   *    future: []
   *  };
   * });
   */
  clear(customUpdateFn?: (history: History<AbstractGitEntityState>) => History<AbstractGitEntityState>) {
    this.history = isFunction(customUpdateFn)
      ? customUpdateFn(this.history)
      : {
          past: [],
          present: null,
          future: [],
        };
    this.updateHasHistory();
  }

  destroy(clearHistory = false) {
    if (clearHistory) {
      this.clear();
    }
    this.subscription.unsubscribe();
  }

  ignoreNext() {
    this.skip = true;
  }

  private get property() {
    return this.params.watchProperty;
  }

  /* Updates the hasPast$ hasFuture$ observables*/
  private updateHasHistory() {
    this.hasFutureSubject.next(this.hasFuture);
    this.hasPastSubject.next(this.hasPast);
  }

  private update(action = OperationType.Undo) {
    this.skipUpdate = true;
    logAction(`@StateHistoryGitState - ${action}`);

    const prevEntities =
      action === OperationType.Undo ? this.history.future[0]?.entities : this.history.past[0]?.entities;

    const currentEntities = this.history.present!.entities;

    if (!prevEntities || !currentEntities) {
      console.log('No history');
      return;
    }

    const prevEntitiesArray = Object.values(prevEntities);
    const currentEntitiesArray = Object.values(currentEntities);
    const differenceInEntityCount = prevEntitiesArray.length - currentEntitiesArray.length;

    if (differenceInEntityCount > 0) {
      const entities = differenceWith(prevEntitiesArray, currentEntitiesArray, (a, b) => a.$path === b.$path);
      this.store.delete(entities.map((e) => e.$path));
    } else {
      const entities = differenceWith(currentEntitiesArray, prevEntitiesArray, isEqual);
      this.store.upsertMany(entities);
    }

    this.updateHasHistory();
    this.skipUpdate = false;
  }
}
